import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, ShieldAlert, Clock, CheckCircle2, FileText } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const fileToB64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function CustomerKYC() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [pan, setPan] = useState("");
  const [aadhaar, setAadhaar] = useState("");
  const [address, setAddress] = useState("");
  const [panImg, setPanImg] = useState("");
  const [aadhaarImg, setAadhaarImg] = useState("");
  const [selfie, setSelfie] = useState("");
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/kyc/me").then((r) => setExisting(r.data));
  }, []);

  const onFile = async (setter, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Max 2MB");
    const b64 = await fileToB64(f);
    setter(b64);
  };

  const submit = async () => {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) return toast.error("Invalid PAN format (e.g. ABCDE1234F)");
    if (!/^[0-9]{12}$/.test(aadhaar)) return toast.error("Aadhaar must be 12 digits");
    if (!address.trim()) return toast.error("Enter your address");
    setLoading(true);
    try {
      await api.post("/kyc/submit", {
        pan_number: pan, aadhaar_number: aadhaar, address,
        pan_image_b64: panImg, aadhaar_image_b64: aadhaarImg, selfie_b64: selfie,
      });
      await refreshUser();
      const r = await api.get("/kyc/me");
      setExisting(r.data);
      toast.success("KYC submitted! Approval in 24 hours.");
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
    setLoading(false);
  };

  const StatusCard = () => {
    const s = user?.kyc_status;
    if (s === "approved") return (
      <div className="gf-card p-6 flex items-center gap-4 bg-[#FCEDED] border-[#3F6B3F]">
        <CheckCircle2 className="w-10 h-10 text-[#3F6B3F]" />
        <div>
          <div className="font-display text-xl text-[#D71921]">KYC Approved</div>
          <p className="text-sm text-[#1F1816] mt-1">You can now browse and purchase lots.</p>
          <button onClick={() => navigate("/lots")} data-testid="kyc-browse-btn"
            className="mt-3 px-4 py-2 rounded-xl bg-[#D71921] text-white text-sm">Browse lots</button>
        </div>
      </div>
    );
    if (s === "pending") return (
      <div className="gf-card p-6 flex items-center gap-4 bg-blue-50 border-blue-200">
        <Clock className="w-10 h-10 text-blue-600" />
        <div>
          <div className="font-display text-xl">KYC Under Review</div>
          <p className="text-sm text-[#1F1816] mt-1">It will take up to <b>24 hours</b> for the admin team to verify your details.</p>
        </div>
      </div>
    );
    if (s === "rejected") return (
      <div className="gf-card p-6 flex items-center gap-4 bg-red-50 border-red-200">
        <ShieldAlert className="w-10 h-10 text-red-600" />
        <div>
          <div className="font-display text-xl">KYC Rejected</div>
          <p className="text-sm text-[#1F1816] mt-1">{existing?.rejection_reason || "Please re-submit your KYC with valid documents."}</p>
        </div>
      </div>
    );
    return null;
  };

  return (
    <div>
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-3xl mb-8 p-8 md:p-10 text-white"
        style={{ background: "linear-gradient(135deg, #D71921 0%, #B0151E 60%, #7A1010 100%)" }}>
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F2C97A]/25 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-[#3F6B3F]/20 blur-3xl" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-xs font-bold uppercase tracking-widest mb-3">
            Step 1 of 3
          </div>
          <h1 className="font-display text-3xl md:text-5xl tracking-tight">KYC Verification</h1>
          <p className="text-white/85 mt-2">Submit your PAN and Aadhaar to start investing. Verification takes up to <b className="text-[#F2C97A]">24 hours</b>.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <StatusCard />
          {(user?.kyc_status === "not_submitted" || user?.kyc_status === "rejected") && (
            <div className="gf-card p-6 md:p-8 space-y-5 mt-6">
              <div className="grid md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium">PAN number</span>
                  <input data-testid="kyc-pan-input" maxLength={10} placeholder="ABCDE1234F"
                    className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-2.5 uppercase outline-none focus:border-[#D71921]"
                    value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Aadhaar number</span>
                  <input data-testid="kyc-aadhaar-input" maxLength={12} placeholder="123412341234"
                    className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-2.5 outline-none focus:border-[#D71921]"
                    value={aadhaar} onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, ""))} />
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-medium">Address</span>
                <textarea data-testid="kyc-address-input" rows={3}
                  className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-2.5 outline-none focus:border-[#D71921]"
                  value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Your full residential address" />
              </label>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  ["PAN photo", panImg, setPanImg, "kyc-pan-img"],
                  ["Aadhaar photo", aadhaarImg, setAadhaarImg, "kyc-aadhaar-img"],
                  ["Selfie", selfie, setSelfie, "kyc-selfie-img"],
                ].map(([label, val, setter, tid]) => (
                  <label key={label} className="block cursor-pointer">
                    <span className="text-sm font-medium">{label}</span>
                    <div className="mt-1 rounded-xl border-2 border-dashed border-[#E8E0D2] p-3 text-center hover:border-[#D71921] transition-colors">
                      {val ? (
                        <img src={val} alt={label} className="w-full h-24 object-cover rounded-lg" />
                      ) : (
                        <div className="py-6 text-[#6B5F58]">
                          <Upload className="w-5 h-5 mx-auto mb-1" />
                          <div className="text-xs">Upload</div>
                        </div>
                      )}
                      <input data-testid={tid} type="file" accept="image/*" className="hidden"
                        onChange={(e) => onFile(setter, e)} />
                    </div>
                  </label>
                ))}
              </div>
              <button data-testid="kyc-submit-btn" onClick={submit} disabled={loading}
                className="w-full md:w-auto px-6 py-3 rounded-xl bg-[#D71921] text-white font-medium disabled:opacity-60">
                {loading ? "Submitting..." : "Submit for verification"}
              </button>
            </div>
          )}
        </div>

        <div className="gf-card p-6 h-fit">
          <FileText className="w-6 h-6 text-[#3F6B3F]" />
          <div className="font-display text-lg mt-3">What happens next?</div>
          <ul className="text-sm text-[#6B5F58] space-y-2 mt-3">
            <li>1. Documents reach our admin desk.</li>
            <li>2. We verify within <b>24 hours</b>.</li>
            <li>3. You unlock the lot marketplace.</li>
            <li>4. Pick a lot, fund via Razorpay.</li>
            <li>5. We raise &amp; sell &mdash; you profit.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
